import { Fragment, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, UserIcon, EnvelopeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { colorMap } from '../../../Common'

const ModalUsuarios = ({ states, setters, action, color = 'blue' }) => {
    const colorClasses = colorMap[color] || colorMap.blue

    const [email, setEmail] = useState('')
    const [errorEmail, setErrorEmail] = useState(false)
    const [errorEmailMsg, setErrorEmailMsg] = useState('Campo obrigatório')
    const [permissao, setPermissao] = useState('')
    const [errorPermissao, setErrorPermissao] = useState(false)

    const handleSubmit = async () => {
        let error = false

        if (email === '') {
            setErrorEmail(true)
            error = true
        } else setErrorEmail(false)

        if (permissao === '') {
            setErrorPermissao(true)
            error = true
        } else setErrorPermissao(false)

        const regexValidEmail = /^[a-z0-9.]+@[a-z0-9]+\.[a-z]+(\.[a-z]+)?$/i
        if (!regexValidEmail.test(email)) {
            setErrorEmail(true)
            setErrorEmailMsg('E-mail inválido')
            error = true
        } else setErrorEmail(false)

        if (error) {
            error = false
            return
        }

        action({
            email,
            role: permissao
        })
    }

    useEffect(() => {
        if (states.modalUsuarios === false) {
            setEmail('')
            setErrorEmailMsg('Campo obrigatório')
            setErrorEmail(false)
            setPermissao('')
            setErrorPermissao(false)
        } else {
            if (states.isEdit) {
                setEmail(states.Usuarios.email)
                setPermissao(`${states.Usuarios.role}`)
            }
        }
    }, [states.modalUsuarios])

    const getRoleLabel = (role) => {
        switch (role) {
            case '0':
                return 'Administrador'
            case '1':
                return 'Gerente'
            case '2':
                return 'Operacional'
            default:
                return 'Selecione uma permissão'
        }
    }

    return (
        <Transition.Root show={states.modalUsuarios} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setters.setModalUsuarios(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center">
                                        <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${colorClasses.headerIconBg} mr-4`}>
                                            <UserIcon className={`h-6 w-6 ${colorClasses.headerIconText}`} />
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="text-2xl font-bold text-gray-900">
                                                {states.isEdit ? 'Editar Usuário' : 'Novo Usuário'}
                                            </Dialog.Title>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {states.isEdit ? `Editando: ${states.Usuarios.email}` : 'Cadastre um novo usuário no sistema'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setters.setModalUsuarios(false)}
                                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>

                                {/* Form */}
                                <div className="space-y-6">
                                    {/* E-mail Field */}
                                    <div>
                                        <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                                            <div className="flex items-center">
                                                <EnvelopeIcon className="w-4 h-4 mr-2 text-gray-500" />
                                                E-mail
                                            </div>
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={email}
                                            maxLength={255}
                                            disabled={states.isEdit}
                                            onChange={e => {
                                                if (e.target.value === '') {
                                                    setErrorEmail(true)
                                                    setErrorEmailMsg('Campo obrigatório')
                                                } else {
                                                    setErrorEmail(false)
                                                }
                                                setEmail(e.target.value)
                                            }}
                                            className={`mt-1 block w-full px-4 py-3 rounded-xl border ${errorEmail
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : `${colorClasses.inputBorder} focus:ring-blue-500 focus:border-blue-500`
                                                } ${states.isEdit ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'} shadow-sm transition-all duration-200 focus:outline-none focus:ring-2`}
                                            placeholder="exemplo@email.com"
                                        />
                                        {errorEmail && (
                                            <p className="mt-2 text-sm text-red-600 flex items-center">
                                                <span className="mr-1">⚠</span>
                                                {errorEmailMsg}
                                            </p>
                                        )}
                                    </div>

                                    {/* Permissão Field */}
                                    <div>
                                        <label htmlFor="permissao" className="block text-sm font-semibold text-gray-700 mb-2">
                                            <div className="flex items-center">
                                                <ShieldCheckIcon className="w-4 h-4 mr-2 text-gray-500" />
                                                Permissão
                                            </div>
                                        </label>
                                        <select
                                            id="permissao"
                                            name="permissao"
                                            value={permissao}
                                            onChange={e => {
                                                if (e.target.value === '') {
                                                    setErrorPermissao(true)
                                                } else {
                                                    setErrorPermissao(false)
                                                }
                                                setPermissao(e.target.value)
                                            }}
                                            className={`mt-1 block w-full px-4 py-3 rounded-xl border ${errorPermissao
                                                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                                : `${colorClasses.inputBorder} focus:ring-blue-500 focus:border-blue-500`
                                                } bg-white shadow-sm transition-all duration-200 focus:outline-none focus:ring-2`}
                                        >
                                            <option value="">Selecione uma permissão</option>
                                            <option value="0">Administrador</option>
                                            <option value="1">Gerente</option>
                                            <option value="2">Operacional</option>
                                        </select>
                                        {errorPermissao && (
                                            <p className="mt-2 text-sm text-red-600 flex items-center">
                                                <span className="mr-1">⚠</span>
                                                Campo obrigatório
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="mt-8 flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => setters.setModalUsuarios(false)}
                                        className="px-6 py-2.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-200"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        type="button"
                                        className={`px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r ${colorClasses.buttonPrimary} ${colorClasses.buttonPrimaryHover} shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105`}
                                    >
                                        {states.isEdit ? 'Salvar Alterações' : 'Cadastrar Usuário'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalUsuarios
